/**
 * TanStack Query hooks for `report-export`. Thin wrappers over ./api.
 * List polls every 5s so running jobs surface ready/failed transitions.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	create,
	downloadExport,
	list,
	queueExport,
	remove,
	reportExportQueryKeys,
	type ExportJob,
	type ListExportsResponse,
	type QueueExportBody,
} from "./api";

export function useReportExportList(): UseQueryResult<ListExportsResponse, Error> {
	return useQuery({
		queryKey: reportExportQueryKeys.list(),
		queryFn: list,
		refetchInterval: (q) => {
			const data = q.state.data as ListExportsResponse | undefined;
			if (!data) return 5000;
			const active = data.items.some(
				(i) => i.status === "queued" || i.status === "running",
			);
			return active ? 5000 : false;
		},
	});
}

export function useReportExportCreate(): UseMutationResult<ExportJob, Error, QueueExportBody> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body: QueueExportBody) => queueExport(body),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: reportExportQueryKeys.all });
		},
	});
}

export function useReportExportDelete(): UseMutationResult<{ removed: boolean }, Error, string> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: remove,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: reportExportQueryKeys.all });
		},
	});
}

export function useReportExportDownload(): UseMutationResult<{ url: string }, Error, string> {
	return useMutation({
		mutationFn: (id: string) => downloadExport(id),
	});
}

// Keep legacy `create` export path resolvable for any shared utilities.
export { create };
